import { normalizedUuidSchema } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { useParams } from 'react-router';
import { isMyOrderNotFound, useMyOrderDetailQuery } from '@/entities/order';
import { useCancelOrder } from '@/features/order-cancel';
import { useRequestOrderCancel } from '@/features/order-cancel-request';
import { useUpdateOrderAddress } from '@/features/order-shipping-address';
import { myQuoteDetailPath } from '@/shared/config';
import { SitePageShell } from '@/shared/ui';
import {
  MyOrderDetail,
  MyOrderDetailSkeleton,
  MyOrderLoadError,
  MyOrderNotFound,
} from '@/widgets/my-order-detail';
import { MyOrderInquiries } from '@/widgets/my-order-inquiries';
import { SiteFooter } from '@/widgets/site-footer';
import { SiteHeader } from '@/widgets/site-header';

/**
 * 내 주문 상세 페이지(주문 설계 §8-2) — 내 견적 상세 페이지와 같은 틀. 주소의 주문 id 로 내 주문 상세를 읽고 결제 전 취소·
 * 취소 요청·배송지 변경 쓰기 훅을 위젯에 잇는다. 없는 주문·남의 주문·주소와 다른 응답은 같은 못 찾음 화면이다.
 * 인쇄 때는 견적서만 남도록 헤더·푸터를 숨긴다.
 */
export const MyOrderDetailPage = (): ReactElement => {
  const { orderId = '' } = useParams();
  const parsed = normalizedUuidSchema.safeParse(orderId);
  const id = parsed.success ? parsed.data : '';
  const query = useMyOrderDetailQuery(id, { enabled: parsed.success });
  const cancelOrder = useCancelOrder();
  const cancelRequest = useRequestOrderCancel();
  const addressChange = useUpdateOrderAddress();
  const order = query.data;

  const body = ((): ReactElement => {
    if (
      !parsed.success ||
      isMyOrderNotFound(query.error) ||
      (order && order.id.toLowerCase() !== id)
    ) {
      return <MyOrderNotFound />;
    }
    if (query.isPending) {
      return <MyOrderDetailSkeleton />;
    }
    if (!order) {
      return <MyOrderLoadError onRetry={() => void query.refetch()} />;
    }
    return (
      <>
        <MyOrderDetail
          key={order.id}
          order={order}
          onCancelUnpaid={cancelOrder.cancel}
          onRequestCancel={cancelRequest.requestCancel}
          onUpdateAddress={addressChange.updateAddress}
          onReload={() => void query.refetch()}
          pending={query.isFetching}
          error={query.isError ? '최신 주문을 불러오지 못했습니다.' : null}
          quoteHref={myQuoteDetailPath(order.quoteRequestId)}
        />
        <MyOrderInquiries orderId={order.id} />
      </>
    );
  })();

  return (
    <SitePageShell
      header={
        <div className="print:hidden">
          <SiteHeader />
        </div>
      }
      footer={
        <div className="print:hidden">
          <SiteFooter />
        </div>
      }
      className="pt-10 sm:pt-12 print:p-0"
    >
      {body}
    </SitePageShell>
  );
};
